import { resolveWithOverride, zStage } from '@packages/shared';
import { z } from 'zod';
import { getConfigOverride } from '@/lib/configOverride';

/**
 * Build-time env, validated once at startup so a missing or malformed value fails here rather than
 * on the page that reads it. Every environment that serves admin-web sets these: the deploy
 * scripts, `bun dev` and e2e's app matrix (`e2e/web/apps.ts`). `VITE_DEV_AUTH_BYPASS` stays in
 * `features/auth/devAuth.ts`, which Vite must inline, and `VITE_ALLOW_CONFIG_OVERRIDE` in
 * `lib/configOverride.ts`.
 */
const envSchema = z.object({
  VITE_STAGE: zStage,
  VITE_CONVEX_URL: z.string().startsWith('http'),
  VITE_CDN_URL: z.string().startsWith('http'),
  VITE_DOCKET_VIEWER_URL: z.string().startsWith('http'),
  /**
   * consumer-web's URL. Venues' sites are served by it, so their links take its scheme and port:
   * `http://<name>.zestordering.localhost:4321` locally.
   */
  VITE_CONSUMER_WEB_URL: z.string().startsWith('http'),
  /** The GitHub Actions run number of the CI deploy. Unset in dev, e2e and preview builds. */
  VITE_GITHUB_RUN_NUMBER: z.string().optional(),
});

export const env = envSchema.parse({
  VITE_STAGE: import.meta.env.VITE_STAGE,
  VITE_CONVEX_URL: import.meta.env.VITE_CONVEX_URL,
  VITE_CDN_URL: import.meta.env.VITE_CDN_URL,
  VITE_DOCKET_VIEWER_URL: import.meta.env.VITE_DOCKET_VIEWER_URL,
  VITE_CONSUMER_WEB_URL: import.meta.env.VITE_CONSUMER_WEB_URL,
  VITE_GITHUB_RUN_NUMBER: import.meta.env.VITE_GITHUB_RUN_NUMBER,
});

/** The Convex deployment URL, or the e2e runtime override (see `lib/configOverride.ts`). */
export const convexUrl = resolveWithOverride(env.VITE_CONVEX_URL, getConfigOverride()?.convexUrl);

/** The CDN that serves menu and article images, or the e2e runtime override. */
export const cdnUrl = resolveWithOverride(env.VITE_CDN_URL, getConfigOverride()?.cdnUrl);
